import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin, Check, ArrowUpRight } from "lucide-react";
import Reveal from "./Reveal";
import { gtmEvent } from "@/lib/gtm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["Personenwagens", "Bestelwagens", "Vrachtwagens"];
const FREQ = ["1x per maand", "2-wekelijks", "Dieptereiniging"];

const HOURS = [
  ["Maandag", "8:00 – 18:00"],
  ["Dinsdag", "8:00 – 18:00"],
  ["Woensdag", "8:00 – 18:00"],
  ["Donderdag", "8:00 – 18:00"],
  ["Vrijdag", "8:00 – 18:00"],
  ["Zaterdag", "8:00 – 18:00"],
  ["Zondag", "8:00 – 18:00"],
];

const initialForm = {
  bedrijf: "",
  telefoon: "",
  email: "",
  postcode: "",
  type_voertuig: [],
  regelmaat: "",
  bijkomende_info: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleVoertuig = (val) => {
    setForm((f) => {
      const exists = f.type_voertuig.find((v) => v.type === val);
      if (exists) {
        return { ...f, type_voertuig: f.type_voertuig.filter((v) => v.type !== val) };
      }
      return { ...f, type_voertuig: [...f.type_voertuig, { type: val, aantal: 1 }] };
    });
  };

  const updateAantal = (val, aantal) => {
    setForm((f) => ({
      ...f,
      type_voertuig: f.type_voertuig.map((v) =>
        v.type === val ? { ...v, aantal: Math.max(1, parseInt(aantal || "1", 10)) } : v
      ),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bedrijf || !form.telefoon || !form.email || !form.postcode || form.type_voertuig.length === 0 || !form.regelmaat) {
      setStatus({ state: "error", message: "Vul alle verplichte velden in (selecteer minstens één type voertuig)." });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      gtmEvent("form_submit_success", {
        form_name: "offerte_aanvraag",
        voertuig_types: form.type_voertuig.map((v) => v.type).join(","),
        regelmaat: form.regelmaat,
      });
      setStatus({
        state: "success",
        message: data?.message || "Bedankt voor uw bericht. We nemen zo snel mogelijk contact met u op!",
      });
      setForm(initialForm);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Oeps, er is een fout opgetreden bij het versturen van uw bericht. Probeer het later nog eens.";
      gtmEvent("form_submit_error", { form_name: "offerte_aanvraag", error: msg.slice(0, 100) });
      setStatus({ state: "error", message: msg });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-white">
      <div className="grid lg:grid-cols-2">
        {/* LEFT — Form */}
        <div className="px-5 sm:px-8 lg:px-16 xl:px-24 py-20 md:py-28 border-r border-cgborder">
          <Reveal>
            <p className="eyebrow mb-5" data-testid="contact-form-eyebrow">Contact</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight text-cgdark mb-4">
              Vraag uw <span className="italic font-light">offerte</span> aan.
            </h2>
            <p className="text-cgmuted text-base md:text-lg leading-relaxed mb-12 max-w-md">
              Vul onderstaand formulier in. Wij nemen binnen één werkdag contact
              met u op met een voorstel op maat.
            </p>
          </Reveal>

          <form onSubmit={submit} className="space-y-2" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-x-8">
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mt-4">
                  Bedrijf *
                </label>
                <input
                  className="cg-input"
                  value={form.bedrijf}
                  onChange={update("bedrijf")}
                  required
                  data-testid="contact-input-bedrijf"
                  placeholder="Uw bedrijfsnaam"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mt-4">
                  Telefoon *
                </label>
                <input
                  className="cg-input"
                  value={form.telefoon}
                  onChange={update("telefoon")}
                  required
                  data-testid="contact-input-telefoon"
                  placeholder="+32 ..."
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mt-4">
                  E-mail *
                </label>
                <input
                  className="cg-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  required
                  data-testid="contact-input-email"
                  placeholder="u@bedrijf.be"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mt-4">
                  Postcode *
                </label>
                <input
                  className="cg-input"
                  value={form.postcode}
                  onChange={update("postcode")}
                  required
                  data-testid="contact-input-postcode"
                  placeholder="8560"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mt-4">
                  Regelmaat *
                </label>
                <select
                  className="cg-input cg-select"
                  value={form.regelmaat}
                  onChange={update("regelmaat")}
                  required
                  data-testid="contact-select-regelmaat"
                >
                  <option value="">Maak een keuze...</option>
                  {FREQ.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="pt-6">
              <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mb-4">
                Type voertuig & aantal * <span className="normal-case tracking-normal text-cgmuted/70 font-normal">(meerdere mogelijk)</span>
              </label>
              <div className="grid sm:grid-cols-3 gap-3" data-testid="contact-voertuig-group">
                {TYPES.map((t) => {
                  const selected = form.type_voertuig.find((v) => v.type === t);
                  const checked = !!selected;
                  return (
                    <div
                      key={t}
                      className={`border transition-all duration-300 ${
                        checked ? "border-cgprimary bg-cgprimary/5" : "border-cgborder hover:border-cgdark"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleVoertuig(t)}
                        data-testid={`contact-voertuig-${t.toLowerCase()}`}
                        className="w-full flex items-center gap-3 px-4 py-4 text-left text-cgdark"
                      >
                        <span
                          className={`inline-flex items-center justify-center w-5 h-5 border transition-colors flex-shrink-0 ${
                            checked
                              ? "bg-cgprimary border-cgprimary text-white"
                              : "border-cgborder bg-white text-transparent"
                          }`}
                        >
                          <Check size={14} strokeWidth={3} />
                        </span>
                        <span className="text-[14px] font-semibold">{t}</span>
                      </button>
                      {checked && (
                        <div className="px-4 pb-4 flex items-center gap-3 border-t border-cgprimary/20 pt-3">
                          <label className="text-[11px] font-semibold tracking-[0.15em] uppercase text-cgmuted">
                            Aantal
                          </label>
                          <div className="flex items-center border border-cgborder bg-white">
                            <button
                              type="button"
                              onClick={() => updateAantal(t, selected.aantal - 1)}
                              className="w-8 h-9 flex items-center justify-center text-cgdark hover:bg-cgsurface transition-colors"
                              data-testid={`contact-aantal-min-${t.toLowerCase()}`}
                              aria-label="Verlaag aantal"
                            >
                              −
                            </button>
                            <input
                              type="number"
                              min="1"
                              max="9999"
                              value={selected.aantal}
                              onChange={(e) => updateAantal(t, e.target.value)}
                              className="w-12 h-9 text-center text-sm font-semibold text-cgdark border-l border-r border-cgborder focus:outline-none focus:bg-cgprimary/5"
                              data-testid={`contact-aantal-${t.toLowerCase()}`}
                            />
                            <button
                              type="button"
                              onClick={() => updateAantal(t, selected.aantal + 1)}
                              className="w-8 h-9 flex items-center justify-center text-cgdark hover:bg-cgsurface transition-colors"
                              data-testid={`contact-aantal-plus-${t.toLowerCase()}`}
                              aria-label="Verhoog aantal"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mt-4">
                Bijkomende info
              </label>
              <textarea
                className="cg-input min-h-[100px] resize-y"
                value={form.bijkomende_info}
                onChange={update("bijkomende_info")}
                rows={4}
                data-testid="contact-input-info"
                placeholder="Vertel ons over uw vloot, locatie, of bijzondere wensen ..."
              />
            </div>

            <div className="pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <p className="text-xs text-cgmuted max-w-xs">
                Door dit formulier te versturen gaat u akkoord met ons privacybeleid.
              </p>
              <button
                type="submit"
                className="cg-btn-primary"
                disabled={status.state === "loading"}
                data-testid="contact-submit-button"
              >
                {status.state === "loading" ? "Versturen..." : "Verstuur aanvraag"}
                <ArrowUpRight size={16} />
              </button>
            </div>

            {status.state === "success" && (
              <div
                className="mt-8 border-l-2 border-cgprimary bg-cgsurface p-6 flex items-start gap-3"
                data-testid="contact-success-message"
              >
                <Check size={20} className="text-cgprimary flex-shrink-0 mt-0.5" />
                <p className="text-cgdark text-sm leading-relaxed">{status.message}</p>
              </div>
            )}
            {status.state === "error" && (
              <div
                className="mt-8 border-l-2 border-red-500 bg-red-50 p-6"
                data-testid="contact-error-message"
              >
                <p className="text-red-700 text-sm leading-relaxed">{status.message}</p>
              </div>
            )}
          </form>
        </div>

        {/* RIGHT — Info on dark */}
        <div className="bg-cgdark text-white px-5 sm:px-8 lg:px-16 xl:px-24 py-20 md:py-28 relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-20" />
          <div className="relative">
            <Reveal>
              <p className="eyebrow mb-5" data-testid="contact-info-eyebrow">Contact Us</p>
              <h3 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-10">
                Liever direct <br />
                <span className="text-cgprimary italic font-light">in contact?</span>
              </h3>
            </Reveal>

            <div className="space-y-8">
              <Reveal delay={80}>
                <div className="text-white/50 text-xs uppercase tracking-[0.2em] mb-4">Bel ons rechtstreeks</div>
                <div className="space-y-3" data-testid="contact-team-phones">
                  <a
                    href="tel:+32496614638"
                    className="flex items-center gap-4 group border border-white/10 hover:border-cgprimary p-3 transition-all duration-300"
                    data-testid="contact-phone-gilles"
                  >
                    <img
                      src="/team/gilles.jpg"
                      alt="Gilles Descamps"
                      className="w-11 h-11 rounded-full object-cover border border-white/15 group-hover:border-cgprimary transition-colors"
                    />
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-cgprimary font-semibold">
                        Gilles · CEO
                      </div>
                      <div className="text-lg font-semibold group-hover:text-cgprimary transition-colors">
                        +32 496 61 46 38
                      </div>
                    </div>
                    <Phone size={16} className="text-white/40 group-hover:text-cgprimary transition-colors" />
                  </a>
                  <a
                    href="tel:+32490265588"
                    className="flex items-center gap-4 group border border-white/10 hover:border-cgprimary p-3 transition-all duration-300"
                    data-testid="contact-phone-justine"
                  >
                    <img
                      src="/team/justine.jpg"
                      alt="Justine Verbeke"
                      className="w-11 h-11 rounded-full object-cover border border-white/15 group-hover:border-cgprimary transition-colors"
                    />
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-cgprimary font-semibold">
                        Justine · Sales & Operations
                      </div>
                      <div className="text-lg font-semibold group-hover:text-cgprimary transition-colors">
                        +32 490 26 55 88
                      </div>
                    </div>
                    <Phone size={16} className="text-white/40 group-hover:text-cgprimary transition-colors" />
                  </a>
                </div>
              </Reveal>

              <Reveal delay={120} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-white/15">
                  <Mail size={16} className="text-cgprimary" />
                </div>
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.2em] mb-1">Mail ons</div>
                  <a
                    href="mailto:info@cargidesgroup.be"
                    className="text-xl md:text-2xl font-semibold link-underline"
                    data-testid="contact-email-link"
                  >
                    info@cargidesgroup.be
                  </a>
                </div>
              </Reveal>

              <Reveal delay={160} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-white/15">
                  <MapPin size={16} className="text-cgprimary" />
                </div>
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.2em] mb-1">Vestiging</div>
                  <p className="text-lg font-semibold" data-testid="contact-address">Nieuwstraat 110A<br />8560 Wevelgem</p>
                </div>
              </Reveal>

              <Reveal delay={200} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-white/15">
                  <Clock size={16} className="text-cgprimary" />
                </div>
                <div className="flex-1">
                  <div className="text-white/50 text-xs uppercase tracking-[0.2em] mb-3">Openingsuren</div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 max-w-sm">
                    {HOURS.map(([d, h]) => (
                      <div key={d} className="contents">
                        <span className="text-white/70 text-[13px]">{d}</span>
                        <span className="text-white text-[13px] font-medium">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>

            <Reveal delay={260} className="mt-12 pt-10 border-t border-white/10">
              <div className="text-white/50 text-xs uppercase tracking-[0.2em] mb-4">Volg ons</div>
              <div className="flex items-center gap-3">
                <a
                  href="https://facebook.com/cargidesgroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center border border-white/15 hover:border-cgprimary hover:bg-cgprimary transition-colors"
                  data-testid="social-facebook"
                  aria-label="Facebook"
                >
                  <Facebook size={16} />
                </a>
                <a
                  href="https://instagram.com/cargidesgroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center border border-white/15 hover:border-cgprimary hover:bg-cgprimary transition-colors"
                  data-testid="social-instagram"
                  aria-label="Instagram"
                >
                  <Instagram size={16} />
                </a>
                <a
                  href="https://linkedin.com/company/cargidesgroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center border border-white/15 hover:border-cgprimary hover:bg-cgprimary transition-colors"
                  data-testid="social-linkedin"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={16} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
